import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PAY } from "../constants/testIds2";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { DownloadSimple } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function toCsv(rows) {
  if (!rows.length) return "";
  const keys = Object.keys(rows[0]);
  return [keys.join(","), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))].join("\n");
}

const REPORTS = [
  { key: "pf", label: "Provident Fund (PF)" },
  { key: "esic", label: "ESIC" },
  { key: "pt", label: "Professional Tax" },
  { key: "tds", label: "TDS" },
];

export default function Statutory() {
  const { user, tenantId } = useAuth();
  const [month, setMonth] = useState(currentMonth());
  const [active, setActive] = useState("pf");
  const [report, setReport] = useState(null);

  useEffect(() => {
    const params = { period_month: month };
    if (user?.role === "super_admin" && tenantId) params.tenant_id = tenantId;
    api.get(`/statutory/${active}`, { params }).then(({ data }) => setReport(data)).catch(() => setReport(null));
  }, [month, active, tenantId, user?.role]);

  const download = () => {
    if (!report?.rows?.length) return;
    const blob = new Blob([toCsv(report.rows)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${active}-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Compliance</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Statutory reports</h1>
          <p className="text-sm text-zinc-500 mt-2">Return-ready summaries for India statutory filings · PF, ESIC, PT, TDS.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-sm" data-testid="statutory-month-input" />
          </div>
          <Button onClick={download} disabled={!report?.rows?.length} className="rounded-sm btn-zenith" data-testid="statutory-download-btn">
            <DownloadSimple size={16} className="mr-1.5" /> Download CSV
          </Button>
        </div>
      </div>

      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          {REPORTS.map((r) => <TabsTrigger key={r.key} value={r.key} data-testid={`statutory-tab-${r.key}`} className="rounded-sm">{r.label}</TabsTrigger>)}
        </TabsList>
        {REPORTS.map((r) => (
          <TabsContent key={r.key} value={r.key} className="mt-4">
            {(!report || !report.rows?.length) ? (
              <div className="border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500 rounded-sm">
                {report?.message || "No data for this period. Run payroll first to generate report data."}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm text-zinc-700">Total: <b className="font-mono-plex">{Number(report.total).toLocaleString("en-IN")}</b></div>
                <div className="overflow-x-auto border border-zinc-200 rounded-sm">
                  <table className="hr-table" data-testid={PAY.statutoryTable}>
                    <thead>
                      <tr>{Object.keys(report.rows[0]).map((k) => <th key={k}>{k.replace(/_/g, " ")}</th>)}</tr>
                    </thead>
                    <tbody>
                      {report.rows.map((row, i) => (
                        <tr key={i}>{Object.keys(row).map((k) => <td key={k} className="font-mono-plex text-xs">{row[k]}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
